<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import ConversationIntegrationTabs from './ConversationIntegrationTabs.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    ConversationIntegrationTabs,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeIndex: 0,
      dashboardAppsFetched: false,
      labelsFetchTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    filteredDashboardApps() {
      const currentInboxId = Number(this.currentChat?.inbox_id || 0);
      if (!currentInboxId) {
        return this.dashboardApps || [];
      }

      return (this.dashboardApps || []).filter(dashboardApp => {
        const inboxIds = this.normalizeInboxIds(dashboardApp.inbox_ids);

        // Empty inbox_ids means global app, visible in all inboxes.
        if (!inboxIds.length) return true;
        return inboxIds.includes(currentInboxId);
      });
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.filteredDashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    integrationTabOrderScope() {
      return [
        this.$store.getters.getCurrentAccountId,
        this.currentChat?.inbox_id || this.inboxId || 0,
      ].join(':');
    },
    integrationTabStoredOrder() {
      return (
        this.$store.getters.getUISettings
          ?.conversation_integration_tabs_order?.[
          this.integrationTabOrderScope
        ] || []
      );
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId, previousInboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id': {
      immediate: true,
      handler() {
        this.scheduleLabelsFetch();
        this.activeIndex = 0;
      },
    },
    filteredDashboardApps() {
      if (this.activeIndex > this.filteredDashboardApps.length) {
        this.activeIndex = 0;
      }
    },
  },
  mounted() {
    this.fetchDashboardApps();
  },
  unmounted() {
    this.clearLabelsFetchTimer();
  },
  methods: {
    normalizeInboxIds(inboxIds) {
      if (Array.isArray(inboxIds)) {
        return inboxIds.map(id => Number(id)).filter(Boolean);
      }

      if (typeof inboxIds === 'string' && inboxIds.trim().length) {
        return inboxIds
          .replace(/[{}]/g, '')
          .split(',')
          .map(id => Number(id.trim()))
          .filter(Boolean);
      }

      return [];
    },
    fetchDashboardApps() {
      if (this.dashboardAppsFetched) {
        return;
      }
      this.dashboardAppsFetched = true;
      this.$store.dispatch('dashboardApps/get');
    },
    clearLabelsFetchTimer() {
      if (this.labelsFetchTimer) {
        clearTimeout(this.labelsFetchTimer);
        this.labelsFetchTimer = null;
      }
    },
    scheduleLabelsFetch() {
      this.clearLabelsFetchTimer();
      if (!this.currentChat.id) {
        return;
      }
      this.labelsFetchTimer = window.setTimeout(() => {
        this.labelsFetchTimer = null;
        this.fetchLabels();
      }, 180);
    },
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    onIntegrationTabOrderChange(order) {
      const existingOrder =
        this.$store.getters.getUISettings
          ?.conversation_integration_tabs_order || {};
      this.$store.dispatch('updateUISettings', {
        uiSettings: {
          conversation_integration_tabs_order: {
            ...existingOrder,
            [this.integrationTabOrderScope]: order,
          },
        },
      });
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-background relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
    />
    <ConversationIntegrationTabs
      v-if="filteredDashboardApps.length && currentChat.id"
      :index="activeIndex"
      :tabs="dashboardAppTabs"
      :stored-order="integrationTabStoredOrder"
      @change="onDashboardAppTabChange"
      @order-change="onIntegrationTabOrderChange"
    />
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in filteredDashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="filteredDashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
